/**
 * LocalCSVBarSource — reads bar files from `{dataDir}/bars/`.
 *
 * Filename conventions supported (case-insensitive on the symbol):
 *   bars/{SYMBOL}/{YYYY-MM-DD}_{TF}.csv     ← preferred (per-symbol subdir)
 *   bars/{SYMBOL}_{YYYY-MM-DD}_{TF}.csv     ← flat
 *   bars/{SYMBOL}/{YYYY-MM-DD}.csv          ← timeframe inferred from data
 *   bars/{SYMBOL}_{YYYY-MM-DD}.csv          ← flat, timeframe inferred
 *
 * `{TF}` is one of `1m`, `5m`, `15m`, `1h`, `4h`. If filename lacks a TF and the
 * caller asks for 5m, we'll happily serve any timeframe found at that date
 * (a future enhancement could downsample/aggregate).
 *
 * This is a UNIVERSAL source — works for any platform (NinjaTrader,
 * Quantower, Tradovate, Polygon CSV downloads, manual exports). The user
 * just drops files in the bars folder. No API keys, no network, no
 * platform-specific code.
 *
 * Phase B: this is the ONLY source. Phase C adds NinjaTrader as a
 * higher-priority source so NT users don't have to manually export.
 */

import { parseCsvToBars } from '../csvBarParser';

export class LocalCSVBarSource {
  constructor() {
    this.name = 'Local CSV';
    // Highest numeric priority = lowest precedence — we're the universal
    // fallback, so other sources (NT live, broker APIs) should run first.
    this.priority = 1000;
    this._available = null; // cache the availability check
  }

  /**
   * Available iff the bars/ folder exists and the IPC bridge is wired.
   *
   * Asymmetric caching (matches NinjaTraderBarSource): cache positive results
   * forever, leave negatives uncached so the source auto-recovers when the
   * user changes the data dir or the IPC handler creates the folder mid-session.
   */
  async isAvailable() {
    if (this._available === true) return true;
    if (typeof window === 'undefined' || !window.electronAPI?.bars?.listFiles) {
      this._available = false;
      return false;
    }
    try {
      const res = await window.electronAPI.bars.listFiles();
      const ok = !!res?.success;
      if (ok) this._available = true;
      else this._available = null;
      return ok;
    } catch {
      this._available = null;
      return false;
    }
  }

  /**
   * Status descriptor for the Settings → Charts panel.
   *
   * Messages are template-only — the Settings UI substitutes `{barsDir}`
   * with the actual resolved bars path (it has it from `bars.getDir()` and
   * the source itself doesn't need the IPC roundtrip to render the string).
   *
   * Why `{barsDir}` and not `{dataDir}`: `bars.getDir()` returns the FULL
   * bars folder path (e.g. `…/Journifi AI/bars`), not the parent data dir.
   * The earlier placeholder name was a misnomer that produced a doubled
   * `…/bars/bars/` after substitution.
   */
  describeStatus() {
    if (typeof window === 'undefined' || !window.electronAPI?.bars?.listFiles) {
      return { ready: false, message: 'Bars IPC bridge unavailable' };
    }
    if (this._available !== true) {
      return { ready: false, message: 'No bars/ folder yet — drop CSV files in {barsDir}' };
    }
    return { ready: true, message: 'Reading from {barsDir}' };
  }

  /**
   * Reset the availability cache so the next isAvailable() re-probes.
   * Useful after the user creates the folder or drops files.
   */
  invalidateAvailability() {
    this._available = null;
  }

  /**
   * Locate a CSV for the given request. Tries each filename convention
   * in order, preferring the per-symbol subdir form.
   *
   * @returns {Promise<string|null>} relative path or null
   */
  async _resolvePath({ symbol, dateStr, timeframe }) {
    if (!window.electronAPI?.bars?.listFiles) return null;
    const res = await window.electronAPI.bars.listFiles();
    if (!res?.success) return null;
    const files = (res.files || []).map(f => f.replace(/\\/g, '/'));

    const sym = String(symbol).toUpperCase();
    const candidates = [
      // Preferred: per-symbol subdirectory with explicit timeframe
      `${sym}/${dateStr}_${timeframe}.csv`,
      `${sym.toLowerCase()}/${dateStr}_${timeframe}.csv`,
      // Flat with explicit timeframe
      `${sym}_${dateStr}_${timeframe}.csv`,
      `${sym.toLowerCase()}_${dateStr}_${timeframe}.csv`,
      // Subdir without explicit timeframe
      `${sym}/${dateStr}.csv`,
      `${sym.toLowerCase()}/${dateStr}.csv`,
      // Flat without explicit timeframe
      `${sym}_${dateStr}.csv`,
      `${sym.toLowerCase()}_${dateStr}.csv`,
    ];

    // Case-insensitive match against the actual file list (Windows is
    // case-insensitive but the file names returned might preserve case)
    const norm = f => f.toLowerCase();
    const fileSet = new Set(files.map(norm));
    for (const c of candidates) {
      if (fileSet.has(norm(c))) {
        // Return the actual matched filename (preserve original case)
        return files.find(f => norm(f) === norm(c));
      }
    }
    return null;
  }

  async fetchBars(req) {
    if (!(await this.isAvailable())) return null;
    const path = await this._resolvePath(req);
    if (!path) return null; // No file matched → defer to next source

    try {
      const res = await window.electronAPI.bars.readFile(path);
      if (!res?.success || !res.text) return null;
      const { bars, parsed, skipped, error } = parseCsvToBars(res.text, { label: path });
      if (error || bars.length === 0) {
        console.warn(`[LocalCSVBarSource] ${path}: ${error || 'no rows parsed'} (parsed=${parsed}, skipped=${skipped})`);
        return null;
      }
      console.log(`[LocalCSVBarSource] ${path}: ${parsed} bars parsed (${skipped} rows skipped)`);
      return bars;
    } catch (e) {
      console.warn(`[LocalCSVBarSource] read failed for ${path}:`, e?.message || e);
      return null;
    }
  }
}
